/**
 * LoaderEventMonitor - Компонент для моніторингу подій лоадерів в реальному часі
 * Показує потік подій завантаження, помилки та прогрес лоадерів
 */

import {
    CheckCircleOutlined,
    ClearOutlined,
    ClockCircleOutlined,
    ExclamationCircleOutlined,
    PlayCircleOutlined,
    SyncOutlined,
} from '@ant-design/icons'
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Input,
    Row,
    Select,
    Space,
    Statistic,
    Switch,
    Tag,
    Timeline,
    Typography,
} from 'antd'
import React, { useEffect, useState } from 'react'
import { LoaderEvent, useLoaderProfiler } from './useLoaderProfiler'

const { Text, Title } = Typography
const { Option } = Select
const { Search } = Input

interface LoaderEventMonitorProps {
    maxEvents?: number
    autoScroll?: boolean
    showFilters?: boolean
}

export const LoaderEventMonitor: React.FC<LoaderEventMonitorProps> = ({
    maxEvents = 100,
    autoScroll = true,
    showFilters = true,
}) => {
    const { events, stats, isLoading, currentLoaders, clearEvents } = useLoaderProfiler()
    const [isPaused, setIsPaused] = useState(false)
    const [filteredEvents, setFilteredEvents] = useState<LoaderEvent[]>([])
    const [eventTypeFilter, setEventTypeFilter] = useState<string>('all')
    const [loaderNameFilter, setLoaderNameFilter] = useState<string>('all')
    const [searchQuery, setSearchQuery] = useState<string>('')

    // Фільтрація подій
    useEffect(() => {
        if (isPaused) return

        let filtered = events.slice(-maxEvents)

        // Фільтр по типу події
        if (eventTypeFilter !== 'all') {
            filtered = filtered.filter((event) => event.event === eventTypeFilter)
        }

        // Фільтр по імені лоадера
        if (loaderNameFilter !== 'all') {
            filtered = filtered.filter((event) => event.loaderName === loaderNameFilter)
        }

        // Пошук по тексту
        if (searchQuery) {
            const query = searchQuery.toLowerCase()
            filtered = filtered.filter(
                (event) =>
                    event.loaderName.toLowerCase().includes(query) ||
                    event.event.toLowerCase().includes(query) ||
                    (event.error && event.error.toLowerCase().includes(query)) ||
                    (event.data && JSON.stringify(event.data).toLowerCase().includes(query))
            )
        }

        setFilteredEvents(filtered.reverse())
    }, [events, isPaused, maxEvents, eventTypeFilter, loaderNameFilter, searchQuery])

    const getEventIcon = (event: LoaderEvent) => {
        switch (event.event) {
            case 'start':
                return <PlayCircleOutlined style={{ color: '#1890ff' }} />
            case 'success':
                return <CheckCircleOutlined style={{ color: '#52c41a' }} />
            case 'error':
                return <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            case 'progress':
                return <SyncOutlined spin style={{ color: '#faad14' }} />
            default:
                return <ClockCircleOutlined style={{ color: '#d9d9d9' }} />
        }
    }

    const getEventColor = (eventType: string) => {
        switch (eventType) {
            case 'start':
                return 'blue'
            case 'success':
                return 'green'
            case 'error':
                return 'red'
            case 'progress':
                return 'orange'
            default:
                return 'default'
        }
    }

    const formatEventTime = (timestamp: number) => {
        return new Date(timestamp).toLocaleTimeString()
    }

    const formatDuration = (duration?: number) => {
        if (!duration) return null
        return duration < 1000 ? `${Math.round(duration)}мс` : `${(duration / 1000).toFixed(2)}с`
    }

    const getUniqueLoaderNames = () => {
        const names = new Set(events.map((event) => event.loaderName))
        return Array.from(names).sort()
    }

    const renderEventDetails = (event: LoaderEvent) => {
        const details: React.ReactNode[] = []

        if (event.duration) {
            details.push(
                <Tag key="duration" color="blue">
                    {formatDuration(event.duration)}
                </Tag>
            )
        }

        if (event.error) {
            details.push(
                <Tag key="error" color="red">
                    {event.error}
                </Tag>
            )
        }

        if (event.data && typeof event.data === 'object') {
            const dataString = JSON.stringify(event.data, null, 2)
            if (dataString.length < 100) {
                details.push(
                    <Tag key="data" color="purple">
                        {dataString}
                    </Tag>
                )
            } else {
                details.push(
                    <Tag key="data" color="purple">
                        Data: {Object.keys(event.data).length} свойств
                    </Tag>
                )
            }
        }

        return details
    }

    if (events.length === 0) {
        return (
            <Card>
                <Empty description="Нет событий лоадеров" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
        )
    }

    return (
        <div style={{ padding: '16px' }}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Statistic title="Всього подій" value={events.length} prefix={<ClockCircleOutlined />} />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Активних лоадерів"
                        value={currentLoaders.length}
                        styles={{ content: { color: isLoading ? '#faad14' : '#3f8600' } }}
                        prefix={isLoading ? <SyncOutlined spin /> : <CheckCircleOutlined />}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Успешных"
                        value={events.filter((e) => e.event === 'success').length}
                        styles={{ content: { color: '#3f8600' } }}
                        prefix={<CheckCircleOutlined />}
                    />
                </Col>
                <Col span={6}>
                    <Statistic
                        title="Ошибок"
                        value={events.filter((e) => e.event === 'error').length}
                        styles={{ content: { color: '#cf1322' } }}
                        prefix={<ExclamationCircleOutlined />}
                    />
                </Col>
            </Row>

            {showFilters && (
                <Card size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16} align="middle">
                        <Col span={4}>
                            <Text strong>Фильтры:</Text>
                        </Col>
                        <Col span={4}>
                            <Select
                                value={eventTypeFilter}
                                onChange={setEventTypeFilter}
                                style={{ width: '100%' }}
                                size="small"
                            >
                                <Option value="all">Все события</Option>
                                <Option value="start">Запуск</Option>
                                <Option value="success">Успех</Option>
                                <Option value="error">Ошибка</Option>
                                <Option value="progress">Прогресс</Option>
                            </Select>
                        </Col>
                        <Col span={4}>
                            <Select
                                value={loaderNameFilter}
                                onChange={setLoaderNameFilter}
                                style={{ width: '100%' }}
                                size="small"
                            >
                                <Option value="all">Все лоадеры</Option>
                                {getUniqueLoaderNames().map((name) => (
                                    <Option key={name} value={name}>
                                        {name}
                                    </Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={6}>
                            <Search
                                placeholder="Поиск событий..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                size="small"
                                allowClear
                            />
                        </Col>
                        <Col span={6}>
                            <Space>
                                <Switch
                                    checked={!isPaused}
                                    onChange={(checked) => setIsPaused(!checked)}
                                    checkedChildren="Live"
                                    unCheckedChildren="Pause"
                                />
                                <Button size="small" icon={<ClearOutlined />} onClick={clearEvents}>
                                    Очистить
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            )}

            {currentLoaders.length > 0 && (
                <Alert
                    message="Активные лоадеры"
                    description={
                        <Space wrap>
                            {currentLoaders.map((loaderName) => (
                                <Badge key={loaderName} status="processing" text={loaderName} />
                            ))}
                        </Space>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            )}

            <Card
                title={
                    <Space>
                        <ClockCircleOutlined />
                        <span>События лоадеров</span>
                        <Badge count={filteredEvents.length} showZero />
                        {isPaused && <Tag color="orange">ПАУЗА</Tag>}
                    </Space>
                }
                size="small"
            >
                {filteredEvents.length === 0 ? (
                    <Empty description="Нет событий по фильтру" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <Timeline mode="left" style={{ maxHeight: '400px', overflow: 'auto' }}>
                        {filteredEvents.map((event) => (
                            <Timeline.Item
                                key={event.id}
                                dot={getEventIcon(event)}
                                label={formatEventTime(event.timestamp)}
                            >
                                <Space orientation="vertical" size="small">
                                    <Space>
                                        <Tag color="blue">{event.loaderName}</Tag>
                                        <Tag color={getEventColor(event.event)}>{event.event.toUpperCase()}</Tag>
                                        {renderEventDetails(event)}
                                    </Space>
                                </Space>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                )}
            </Card>
        </div>
    )
}

export default LoaderEventMonitor
